// AI-ASSISTED: Columbia bridge for Vans PE cart drawer (Cursor)

function isPeCartDrawer(drawer) {
  return Boolean(drawer && drawer.querySelector('cart-drawer-items'));
}

function enablePeCartDrawerCloseButtons(drawer) {
  if (!drawer) return;

  // AI: Columbia disableCartButtons() may leave close disabled after last-item remove
  drawer.querySelectorAll('.drawer__close').forEach((button) => {
    button.removeAttribute('disabled');
    button.disabled = false;
  });
}

function syncPeCartDrawerEmptyState() {
  const drawer = document.querySelector('cart-drawer[data-pe-checkout-drawer]');
  if (!drawer) return;

  const hasLineItems = Boolean(
    drawer.querySelector('.cart__item') || drawer.querySelector('[data-api-line-items] .cart__item')
  );
  drawer.classList.toggle('is-empty', !hasLineItems);

  const emptyState = drawer.querySelector('.drawer__inner-empty');
  if (emptyState) {
    emptyState.hidden = hasLineItems;
  }

  if (hasLineItems && typeof drawer.clearEmptyCartDrawerState === 'function') {
    drawer.clearEmptyCartDrawerState();
  }

  enablePeCartDrawerCloseButtons(drawer);
  syncPeCartDrawerErrorState(drawer, hasLineItems);
}

function syncPeCartDrawerErrorState(drawer, hasLineItems) {
  if (!drawer) return;

  const errors = drawer.querySelector('[data-cart-errors]');
  if (!errors) return;

  const message = errors.querySelector('[data-error-message]');
  const hasMessage = Boolean(message && message.textContent.trim());

  if (hasLineItems && !hasMessage) {
    errors.classList.remove('is-expanded');
  }
}

window.syncPeCartDrawerEmptyState = syncPeCartDrawerEmptyState;

function patchPeDrawerColumbiaCartScope() {
  const drawer = document.querySelector('cart-drawer[data-pe-checkout-drawer]');
  if (!drawer) return;

  const holder = drawer.querySelector('[data-items-holder]');
  const errors = drawer.querySelector('[data-cart-errors]');
  const priceHolder = drawer.querySelector('[data-cart-price-holder]');

  document.querySelectorAll('body > cart-items').forEach((orphan) => {
    if (!orphan.hasAttribute('data-items-holder')) {
      orphan.remove();
    }
  });

  drawer.querySelectorAll('cart-items').forEach((el) => {
    if (holder) el.itemsHolder = holder;
    el.cart = drawer;
    if (errors) el.cartErrorHolder = errors;
    if (priceHolder) el.priceHolder = priceHolder;
  });
}

function bindPeDrawerColumbiaCartScope() {
  patchPeDrawerColumbiaCartScope();
  document.addEventListener('cart-drawer:section-updated', patchPeDrawerColumbiaCartScope);
  document.addEventListener('theme:cart:change', patchPeDrawerColumbiaCartScope);
}

function syncColumbiaCartCount(itemCount) {
  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      bubbles: true,
      detail: { cartCount: itemCount },
    })
  );
}

function refreshPeCartDrawerSections(drawer) {
  if (!drawer || typeof drawer.renderContents !== 'function') return Promise.resolve();

  const url = `${window.routes.cart_url}?sections=cart-drawer`;

  return fetch(url)
    .then((response) => response.json())
    .then((sections) => {
      drawer.renderContents({ sections });
      return fetch(`${routes.cart_url}.js`);
    })
    .then((response) => (response && response.ok ? response.json() : null))
    .then((cart) => {
      if (cart && typeof cart.item_count === 'number') {
        syncColumbiaCartCount(String(cart.item_count));
      }
    })
    .catch((error) => {
      console.error('[cart-drawer] section refresh failed', error);
    });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('theme:cart-drawer:show', () => {
      if (!isPeCartDrawer(this)) return;
      this.open(window.a11y?.lastElement || document.querySelector('[data-cart-toggle]'));
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    patchPeDrawerColumbiaCartScope();
    syncPeCartDrawerEmptyState();

    if (typeof window.resetCartDrawerCheckoutSubmitState === 'function') {
      window.resetCartDrawerCheckoutSubmitState();
    }

    if (typeof window.syncCartDrawerStepLayout === 'function') {
      window.syncCartDrawerStepLayout();
    }

    const holder = this.querySelector('[data-items-holder]');
    const headerCount = parseInt(
      document.querySelector('[data-cart-count]')?.getAttribute('data-cart-count') || '0',
      10
    );
    if (holder && !holder.querySelector('.cart__item') && headerCount > 0) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  clearEmptyCartDrawerState() {
    this.classList.remove('is-empty');

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
      // Keep .drawer__inner-empty in DOM — CSS hides it when cart has items
    }

    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
  }

  renderContents(parsedState) {
    this.clearEmptyCartDrawerState();
    this.productId = parsedState.id;

    const sectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    if (sectionHtml) {
      this.getSectionsToRender().forEach((section) => {
        const target = section.scoped
          ? this.querySelector(section.selector)
          : section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

        if (!target) return;

        target.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      });
    }

    if (typeof parsedState.item_count === 'number') {
      syncColumbiaCartCount(String(parsedState.item_count));
    }

    document.dispatchEvent(new CustomEvent('cart-drawer:section-updated'));

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this), { once: true });
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html || !selector) return '';
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer-Step1',
        scoped: true,
      },
      {
        id: 'cart-drawer',
        selector: '#CartDrawer-StickyFooter',
        scoped: true,
      },
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble' });
    }

    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Step1',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-StickyFooter',
      },
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function bindColumbiaCartToggle() {
  document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
    if (toggle.dataset.peCartDrawerBound === 'true') return;
    toggle.dataset.peCartDrawerBound = 'true';
    toggle.addEventListener('click', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (!isPeCartDrawer(drawer)) return;
      event.preventDefault();
      drawer.open(toggle);
    });
  });
}

bindColumbiaCartToggle();
document.addEventListener('shopify:section:load', bindColumbiaCartToggle);

function bindPeCartDrawerStepNavigation() {
  const drawer = document.querySelector('cart-drawer[data-pe-checkout-drawer]');
  if (!drawer) return;

  const goNext = drawer.querySelector('#CartDrawer-GoToCheckoutStep');
  if (goNext && goNext.dataset.peStepBound !== 'true') {
    goNext.dataset.peStepBound = 'true';
    // "Siguiente" es un enlace a /cart: solo se bloquea con el carrito vacío.
    goNext.addEventListener('click', (event) => {
      if (goNext.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }

  const goBack = drawer.querySelector('#CartDrawer-GoToItemsStep');
  if (goBack && goBack.dataset.peStepBound !== 'true') {
    goBack.dataset.peStepBound = 'true';
    goBack.addEventListener('click', (event) => {
      event.preventDefault();
      if (typeof window.cartDrawerShowStep === 'function') {
        window.cartDrawerShowStep(1);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindPeCartDrawerStepNavigation();
    bindPeDrawerColumbiaCartScope();
  });
} else {
  bindPeCartDrawerStepNavigation();
  bindPeDrawerColumbiaCartScope();
}
document.addEventListener('cart-drawer:section-updated', bindPeCartDrawerStepNavigation);

document.addEventListener('theme:cart:change', () => {
  patchPeDrawerColumbiaCartScope();
  syncPeCartDrawerEmptyState();
  if (!document.querySelector('cart-drawer[data-pe-checkout-drawer] .cart__item')) return;
  document.dispatchEvent(new CustomEvent('cart-drawer:section-updated'));
});

document.addEventListener('theme:product:added', () => {
  const drawer = document.querySelector('cart-drawer[data-pe-checkout-drawer]');
  if (!drawer || !isPeCartDrawer(drawer)) return;

  syncPeCartDrawerEmptyState();
  document.dispatchEvent(new CustomEvent('cart-drawer:section-updated'));

  if (!drawer.classList.contains('active')) {
    drawer.open();
  }
});
